import React, { useState } from "react";
import { Helmet } from "react-helmet";
import { Send, Mail, Globe, ShieldCheck, ArrowRight, Activity } from "lucide-react";
import { db } from "../firebase";
import { collection, addDoc, serverTimestamp } from "firebase/firestore";

export default function ContactOmoreGames() {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    subject: "Feedback",
    message: "",
  });

  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (loading) return;

    try {
      setLoading(true);
      await addDoc(collection(db, "contactMessages"), {
        ...formData,
        createdAt: serverTimestamp(),
      });

      setSuccess(true);
      setFormData({ name: "", email: "", subject: "Feedback", message: "" });
      setTimeout(() => setSuccess(false), 5000);
    } catch (err) {
      console.error(err);
      alert("Something went wrong. Please check your internet.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#090a0b] text-white selection:bg-blue-500/30 font-sans">
<Helmet>

  <html lang="en" />

  <title>
    Contact OMORE Games | Neuralis Support, Feedback & Collaboration
  </title>

  <meta
    name="description"
    content="Contact OMORE Games for support, feedback, bug reports, or collaboration. Creator of Neuralis reflex training game, reaction time tests, and browser brain training games."
  />

  <meta
    name="keywords"
    content="
    contact omore games,
    neuralis support,
    omore games email,
    brain game support,
    reflex game contact,
    neuralis bug report,
    reaction time test support,
    omore games collaboration,
    browser games support,
    neuralis help
    "
  />

  <meta name="robots" content="index, follow" />
  <meta name="googlebot" content="index, follow, max-image-preview:large" />

  <meta name="author" content="OMORE Games" />

  <link
    rel="canonical"
    href="https://omore.games/contact"
  />

  {/* Open Graph */}

  <meta property="og:type" content="website" />

  <meta
    property="og:title"
    content="Contact OMORE Games"
  />

  <meta
    property="og:description"
    content="Reach OMORE Games for Neuralis support, feedback, or partnership."
  />

  <meta
    property="og:url"
    content="https://omore.games/contact"
  />

  <meta
    property="og:site_name"
    content="OMORE Games"
  />

  <meta
    property="og:image"
    content="https://omore.games/og-cover.png"
  />

  {/* Twitter */}

  <meta name="twitter:card" content="summary_large_image" />

  <meta
    name="twitter:title"
    content="Contact OMORE Games"
  />

  <meta
    name="twitter:description"
    content="Support page for Neuralis and OMORE Games."
  />

  <meta
    name="twitter:image"
    content="https://omore.games/og-cover.png"
  />

  {/* Schema */}

  <script type="application/ld+json">
    {JSON.stringify({
      "@context": "https://schema.org",
      "@type": "ContactPage",
      name: "Contact OMORE Games",
      url: "https://omore.games/contact",
      about: {
        "@type": "Organization",
        name: "OMORE Games"
      }
    })}
  </script>

</Helmet>
      {/* TOP SECTION */}
      <section className="relative pt-24 pb-16 md:pt-40 md:pb-24 px-6 overflow-hidden">
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-full h-[400px] bg-blue-600/5 blur-[120px] rounded-full" />
        
        <div className="max-w-4xl mx-auto relative z-10 text-center">
          <div className="mb-8 flex justify-center">
            <span className="text-[10px] md:text-xs tracking-[0.6em] text-blue-500 uppercase font-black px-5 py-2 border border-blue-500/20 bg-blue-500/5 rounded-full animate-pulse">
              We are Online
            </span>
          </div>

          <h1 className="text-5xl md:text-8xl font-black tracking-tighter uppercase mb-4 leading-none">
            GET IN <span className="text-zinc-800">TOUCH</span>
          </h1>

          <p className="text-[10px] md:text-xs tracking-[0.4em] text-zinc-600 uppercase font-bold">
            Send us a message or give feedback
          </p>
        </div>
      </section>

      {/* CONTACT FORM AND INFO */}
      <section className="pb-32 px-6">
        <div className="max-w-6xl mx-auto grid lg:grid-cols-12 gap-16">
          
          {/* LEFT SIDE: INFO */}
          <div className="lg:col-span-5 space-y-12">
            <div className="space-y-6">
              <h2 className="text-2xl font-black uppercase tracking-tight">
                Our <span className="text-blue-500">Links</span>
              </h2>
              <div className="w-12 h-1 bg-blue-600" />
              <p className="text-zinc-200 text-sm md:text-base leading-relaxed font-medium max-w-sm">
                Have a question? We check our messages every day and usually reply within 24 hours.
              </p>
            </div>

            <div className="grid gap-4">
              {[
                { icon: <Mail className="w-4 h-4" />, label: "Email Us", value: "omoregamesofficial@gmail.com" },
                { icon: <Globe className="w-4 h-4" />, label: "Visit Website", value: "omkarmore.com" },
                { icon: <ShieldCheck className="w-4 h-4" />, label: "Privacy", value: "Your data is safe" }
              ].map((item, i) => (
                <div key={i} className="group p-5 border border-zinc-800 bg-zinc-900/10 rounded-sm flex items-center gap-6 transition-all hover:border-blue-500/50">
                  <div className="text-blue-500 group-hover:scale-110 transition-transform">{item.icon}</div>
                  <div>
                    <p className="text-[8px] uppercase tracking-[0.2em] text-zinc-600 font-black mb-1">{item.label}</p>
                    <p className="text-[11px] font-bold uppercase tracking-widest text-zinc-200">{item.value}</p>
                  </div>
                </div>
              ))}
            </div>

            {/* RESPONSE TIME CARD */}
            <div className="p-8 border border-zinc-800 bg-[#0d0e10] rounded-sm relative group overflow-hidden">
               <div className="absolute top-0 right-0 p-4 opacity-20">
                  <Activity className="w-12 h-12 text-blue-500" />
               </div>
               <p className="text-[10px] uppercase tracking-[0.3em] text-zinc-600 font-black mb-4">Reply Time</p>
               <div className="text-5xl font-black text-white italic">Under 24<span className="text-blue-500 text-2xl">h</span></div>
               <div className="mt-8 flex gap-1">
                 {[...Array(15)].map((_, i) => (
                   <div key={i} className={`h-1 w-full rounded-full transition-all duration-1000 ${i < 12 ? 'bg-blue-600' : 'bg-zinc-800'}`} />
                 ))}
               </div>
            </div>
          </div>

          {/* RIGHT SIDE: THE FORM */}
          <div className="lg:col-span-7">
            <form onSubmit={handleSubmit} className="p-8 md:p-12 border border-zinc-800 bg-zinc-900/5 rounded-sm space-y-10 relative">
              
              <div className="grid md:grid-cols-2 gap-10">
                <div className="space-y-3">
                  <label className="text-[9px] uppercase tracking-[0.3em] text-zinc-200 font-black ml-1">Your Name</label>
                  <input
                    required
                    type="text"
                    placeholder="NAME"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="w-full bg-black border-zinc-800 border-b p-4 text-xs font-bold tracking-[0.2em] uppercase focus:outline-none focus:border-blue-500 focus:bg-zinc-900/20 transition-all text-white placeholder:text-zinc-800"
                  />
                </div>
                <div className="space-y-3">
                  <label className="text-[9px] uppercase tracking-[0.3em] text-zinc-200 font-black ml-1">Your Email</label>
                  <input
                    required
                    type="email"
                    placeholder="EMAIL@EXAMPLE.COM"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    className="w-full bg-black border-zinc-800 border-b p-4 text-xs font-bold tracking-[0.2em] uppercase focus:outline-none focus:border-blue-500 focus:bg-zinc-900/20 transition-all text-white placeholder:text-zinc-800"
                  />
                </div>
              </div>

              <div className="space-y-3">
                <label className="text-[9px] uppercase tracking-[0.3em] text-zinc-200 font-black ml-1">Subject</label>
                <select
                  value={formData.subject}
                  onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                  className="w-full bg-black border-zinc-800 border-b p-4 text-xs font-bold tracking-[0.2em] uppercase focus:outline-none focus:border-blue-500 transition-all text-white appearance-none cursor-pointer"
                >
                  <option>Bug Report (Found an Error)</option>
                  <option>Business (Work with us)</option>
                  <option>General Feedback</option>
                  <option>Other</option>
                </select>
              </div>

              <div className="space-y-3">
                <label className="text-[9px] uppercase tracking-[0.3em] text-zinc-200 font-black ml-1">Your Message</label>
                <textarea
                  required
                  rows={5}
                  placeholder="WRITE YOUR MESSAGE HERE..."
                  value={formData.message}
                  onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                  className="w-full bg-black border-zinc-800 border-b p-4 text-xs font-bold tracking-[0.2em] uppercase focus:outline-none focus:border-blue-500 focus:bg-zinc-900/20 transition-all text-white placeholder:text-zinc-800 resize-none"
                />
              </div>

              <button
                disabled={loading}
                className="group w-full flex items-center justify-between px-10 py-6 bg-blue-600 hover:bg-blue-500 disabled:bg-zinc-800 text-white transition-all shadow-2xl shadow-blue-900/20 rounded-sm"
              >
                <span className="text-[10px] md:text-xs tracking-[0.4em] uppercase font-black">
                  {loading ? "Sending..." : success ? "Message Sent!" : "Send Message"}
                </span>
                <Send className={`w-4 h-4 transition-transform ${loading ? 'animate-ping' : 'group-hover:translate-x-2'}`} />
              </button>

              {success && (
                <div className="absolute inset-0 bg-black/95 flex items-center justify-center p-12 text-center animate-in fade-in duration-500">
                  <div className="space-y-4">
                    <div className="w-16 h-16 bg-blue-500/20 border border-blue-500 rounded-full flex items-center justify-center mx-auto mb-6">
                      <ShieldCheck className="w-8 h-8 text-blue-500" />
                    </div>
                    <h3 className="text-xl font-black uppercase tracking-widest">Message Sent</h3>
                    <p className="text-xs text-zinc-200 tracking-widest uppercase">Thank you! We will get back to you soon.</p>
                  </div>
                </div>
              )}
            </form>
          </div>
        </div>
      </section>

      {/* FOOTER LINE */}
      <section className="py-24 border-t border-zinc-900 text-center">
         <p className="text-[10px] md:text-xs tracking-[1em] uppercase text-zinc-800 font-black">
           End of Page
         </p>
      </section>
    </div>
  );
}